import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAccessToken, selectLocation, selectUser } from "../store/auth";
import { apiFetch } from "../common/api";
import { formatObject } from "../common/formatting";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import ConfirmDialog from "../components/ConfirmDialog";
import toast from "react-hot-toast";

const HOST = import.meta.env.VITE_API_HOST;
const SUPER_USER_ROLE = 2;

/**
 * Transaction review page for Bitrix24-driven cancellation.
 * Any staff member can view the details; only super-users may cancel.
 */
export default function TransactionReview() {
  const { id } = useParams();
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const managerLocation = useSelector(selectLocation);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const routeLocation = useLocation();

  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isSuperUser = user?.role === SUPER_USER_ROLE;

  const loadTransaction = useCallback(async () => {
    try {
      const resp = await apiFetch(`${HOST}/api/v1/review/transaction/${id}`);
      if (resp.status === 403) {
        setError(t("review.forbidden"));
        return;
      }
      if (resp.status === 404) {
        setError(t("review.notFound"));
        return;
      }
      if (!resp.ok) {
        setError(t("review.loadError"));
        return;
      }
      setTx(await resp.json());
      setError(null);
    } catch {
      setError(t("review.networkError"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (!accessToken) {
      // Keep the review link across the login round-trip: without it the
      // operator lands on the dashboard and the cancellation is lost (#629).
      const target = encodeURIComponent(
        routeLocation.pathname + routeLocation.search,
      );
      navigate(`/login?redirect_to=${target}`, { replace: true });
      return;
    }

    loadTransaction();
  }, [
    accessToken,
    loadTransaction,
    routeLocation.pathname,
    routeLocation.search,
    navigate,
  ]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const resp = await apiFetch(
        `${HOST}/api/v1/review/transaction/${id}/cancel`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        },
      );
      const data = await resp.json();
      if (resp.ok) {
        toast.success(data.message || t("review.cancelled"));
        setShowConfirm(false);
        await loadTransaction();
      } else {
        toast.error(data.detail || t("review.cancelError"));
      }
    } catch {
      toast.error(t("review.networkError"));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-c-gray-400">
        {t("common.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg bg-c-gray-600 p-6 text-center">
          <div className="mb-4 text-lg text-c-red-500">{error}</div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh py-8">
      <div className="mx-auto w-full max-w-2xl px-4">
        <div className="overflow-hidden rounded-lg bg-c-gray-600">
          <div className="flex items-center justify-between gap-4 border-b border-c-gray-400/20 px-6 py-4">
            <h1 className="text-2xl font-semibold">
              {t("review.title", { id: tx.transactionId })}
            </h1>
            <StatusBadge status={tx.status} />
          </div>

          <div className="space-y-3 px-6 py-4">
            <DetailRow label={t("review.type")} value={tx.reasonLabel} />
            <DetailRow label={t("review.location")} value={tx.locationName} />
            <DetailRow label={t("review.admin")} value={tx.adminName} />
            <DetailRow label={t("review.customer")} value={tx.customerName} />
            <DetailRow
              label={t("review.phone")}
              value={
                tx.customerPhone
                  ? `****${tx.customerPhone.slice(-4)}`
                  : undefined
              }
            />
            <DetailRow
              label={t("review.minutes")}
              value={formatMinutes(tx.minutesAmount, t)}
            />
            <DetailRow
              label={t("review.amount")}
              value={formatObject(tx.priceRub, "priceRub")}
            />
            <DetailRow
              label={t("review.balance")}
              value={formatBalance(tx.balance, tx.debt, t)}
            />
            <DetailRow
              label={t("review.managerComment")}
              value={tx.managerComment}
            />
            <DetailRow
              label={t("review.adminComment")}
              value={tx.cancellationReason}
            />
            <DetailRow
              label={t("review.date")}
              value={formatObject(tx.createdAt, "datetime")}
            />
          </div>

          {tx.status === "Succeed" && (
            <div className="space-y-2 border-t border-c-gray-400/20 px-6 py-4">
              <Button
                variant="destructive"
                className="w-full py-2"
                disabled={!isSuperUser}
                onClick={() => setShowConfirm(true)}
              >
                {t("review.cancelTransaction")}
              </Button>
              {!isSuperUser && (
                <p className="text-center text-sm text-c-gray-400">
                  {t("review.superUserOnly")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={isSuperUser && showConfirm}
        onOpenChange={setShowConfirm}
        title={t("review.confirmTitle")}
        confirmVariant="destructive"
        confirmText={
          cancelling ? t("review.cancelling") : t("review.confirmAction")
        }
        disabled={cancelling}
        onConfirm={handleCancel}
        customContent={
          <div className="space-y-4">
            <p className="text-c-gray-300">{t("review.confirmMessage")}</p>

            <div className="space-y-2 rounded-lg bg-c-gray-500 p-3 text-sm">
              <DetailRow label={t("review.type")} value={tx.reasonLabel} />
              <DetailRow label={t("review.location")} value={tx.locationName} />
              <DetailRow
                label={t("review.minutes")}
                value={formatMinutes(tx.minutesAmount, t)}
              />
              <DetailRow
                label={t("review.amount")}
                value={formatObject(tx.priceRub, "priceRub")}
              />
            </div>

            {isLocationMismatch(managerLocation, tx.locationName) && (
              <div className="rounded-lg border border-c-red-500/30 bg-c-red-500/20 p-3 text-sm text-c-red-500">
                {t("review.locationMismatch", {
                  transactionLocation: tx.locationName,
                  userLocation: managerLocation.name,
                })}
              </div>
            )}

            {tx.managerComment && (
              <div>
                <p className="mb-1 text-sm font-medium text-c-gray-300">
                  {t("review.managerComment")}
                </p>
                <p className="rounded-lg bg-c-gray-500 p-3 text-sm text-white">
                  {tx.managerComment}
                </p>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const styles = {
    Succeed: "bg-[#66DA5C]/15 text-[#66DA5C]",
    RolledBack: "bg-c-red-500/20 text-c-red-500",
  };
  const labels = {
    Succeed: t("review.status.active"),
    RolledBack: t("review.status.cancelled"),
  };

  return (
    <span
      className={`whitespace-nowrap rounded px-2 py-1 text-xs font-medium ${
        styles[status] || "bg-c-gray-500 text-c-gray-400"
      }`}
    >
      {labels[status] || status}
    </span>
  );
}

function DetailRow({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="flex justify-between gap-4">
      <span className="text-c-gray-400">{label}</span>
      <span className="text-right font-medium text-white">{value}</span>
    </div>
  );
}

function formatMinutes(amount, t) {
  return `${amount > 0 ? "+" : ""}${amount} ${t("common.minutes")}`;
}

function formatBalance(balance, debt, t) {
  const base = `${balance} ${t("common.minutes")}`;
  return debt > 0
    ? `${base} (${t("review.debt")} ${debt} ${t("common.minutes")})`
    : base;
}

function isLocationMismatch(managerLocation, transactionLocation) {
  return (
    managerLocation != null && managerLocation.name !== transactionLocation
  );
}
